import {
  boolean,
  createSchema,
  json,
  number,
  relationships,
  string,
  table,
  type Row,
} from '@rocicorp/zero'

// ─────────────────────────────────────────────────────────────────────────────
// Tables. TypeScript uses camelCase; `.from('snake_case')` maps to the Postgres
// column/table names in db/migrations/0001_init.sql. Timestamps are declared as
// number() — Zero replicates Postgres timestamptz to clients as epoch-ms numbers.
//
// NOTE: member data is tiered by how widely it may travel, because Zero
// authorizes ROWS, not columns — a query returns every declared column of a
// table, so a sensitivity tier has to be a table:
//
//   users          public roster            — every authenticated role
//   users_private  full name, email, DOB    — owner or admin, roster-wide
//   pins           the PIN namespace        — owner or admin; the reserved
//                                             kiosk row is filtered out by the
//                                             named query, never synced
//   users_profile  welfare + kit detail     — owner or admin, ONE member at a
//                                             time (the edit form only)
//
// auth_sessions and session hashes remain absent from the schema entirely.
// ─────────────────────────────────────────────────────────────────────────────

const users = table('users')
  .columns({
    id: string(),
    displayName: string().from('display_name'),
    nameOnBack: string().from('name_on_back').optional(),
    numberOnBack: number().from('number_on_back').optional(),
    buttonPosition: number().from('button_position').optional(),
    isAdmin: boolean().from('is_admin'),
    isApproved: boolean().from('is_approved'),
    createdAt: number().from('created_at'),
    deletedAt: number().from('deleted_at').optional(),
  })
  .primaryKey('id')

const usersPrivate = table('usersPrivate')
  .from('users_private')
  .columns({
    userId: string().from('user_id'),
    fullName: string().from('full_name').optional(),
    email: string().optional(),
    // Postgres `date` replicates as epoch-ms, like every other timestamp here.
    dateOfBirth: number().from('date_of_birth').optional(),
  })
  .primaryKey('userId')

// Member PINs and the reserved kiosk PIN share one namespace, so the primary
// key makes a collision impossible — no trigger, no procedural check. The
// reserved row (user_id null) is excluded by the `assignedPins` query.
const pins = table('pins')
  .columns({
    pinCode: string().from('pin_code'),
    userId: string().from('user_id').optional(),
    reason: string().optional(),
  })
  .primaryKey('pinCode')

const usersProfile = table('usersProfile')
  .from('users_profile')
  .columns({
    userId: string().from('user_id'),
    address: string().optional(),
    mobileNumber: string().from('mobile_number').optional(),
    medicalInfo: string().from('medical_info').optional(),
    emergencyContactName: string().from('emergency_contact_name').optional(),
    emergencyContactPhone: string().from('emergency_contact_phone').optional(),
    shirtSize: string().from('shirt_size').optional(),
    shortSize: string().from('short_size').optional(),
    shoeSize: string().from('shoe_size').optional(),
  })
  .primaryKey('userId')

const drinks = table('drinks')
  .columns({
    drinkId: string().from('drink_id'),
    description: string(),
    priceCents: number().from('price_cents'),
    buttonPosition: number().from('button_position').optional(),
    createdAt: number().from('created_at'),
    deletedAt: number().from('deleted_at').optional(),
  })
  .primaryKey('drinkId')

const guests = table('guests')
  .columns({
    id: string(),
    userId: string().from('user_id'),
    name: string(),
    buttonPosition: number().from('button_position').optional(),
    createdAt: number().from('created_at'),
    deletedAt: number().from('deleted_at').optional(),
  })
  .primaryKey('id')

const payments = table('payments')
  .columns({
    paymentId: string().from('payment_id'),
    userId: string().from('user_id'),
    amountCents: number().from('amount_cents'),
    stripeCheckoutId: string().from('stripe_checkout_id').optional(),
    paymentMethod: string().from('payment_method'),
    createdAt: number().from('created_at'),
    confirmedAt: number().from('confirmed_at').optional(),
    cancelledAt: number().from('cancelled_at').optional(),
  })
  .primaryKey('paymentId')

const userDrinks = table('userDrinks')
  .from('user_drinks')
  .columns({
    id: string(),
    userId: string().from('user_id'),
    guestId: string().from('guest_id').optional(),
    drinkId: string().from('drink_id'),
    purchasedAt: number().from('purchased_at'),
    paidAt: number().from('paid_at').optional(),
    paymentId: string().from('payment_id').optional(),
  })
  .primaryKey('id')

const fees = table('fees')
  .columns({
    id: string(),
    userId: string().from('user_id'),
    amountCents: number().from('amount_cents'),
    description: string().optional(),
    type: string(),
    createdAt: number().from('created_at'),
    feeDate: number().from('fee_date'),
    deletedAt: number().from('deleted_at').optional(),
    paidAt: number().from('paid_at').optional(),
    paymentId: string().from('payment_id').optional(),
  })
  .primaryKey('id')

const credits = table('credits')
  .columns({
    id: string(),
    userId: string().from('user_id'),
    paymentId: string().from('payment_id').optional(),
    amountCents: number().from('amount_cents'),
    // What is left to spend. A credit may be consumed partially (US-16), so the
    // balance subtracts this, not amountCents. usedAt is set exactly when it
    // reaches zero.
    remainingAmountCents: number().from('remaining_amount_cents'),
    description: string().optional(),
    createdAt: number().from('created_at'),
    usedAt: number().from('used_at').optional(),
  })
  .primaryKey('id')

const orderActivity = table('orderActivity')
  .from('order_activity')
  .columns({
    id: string(),
    userId: string().from('user_id'),
    memberName: string().from('member_name'),
    guestName: string().from('guest_name').optional(),
    drinkDescription: string().from('drink_description'),
    purchasedAt: number().from('purchased_at'),
  })
  .primaryKey('id')

const importBatches = table('importBatches')
  .from('import_batches')
  .columns({
    id: string(),
    fileName: string().from('file_name'),
    uploadedBy: string().from('uploaded_by'),
    uploadedAt: number().from('uploaded_at'),
    processedAt: number().from('processed_at').optional(),
    processedCount: number().from('processed_count'),
    sourceSha256: string().from('source_sha256'),
  })
  .primaryKey('id')

const importTransactions = table('importTransactions')
  .from('import_transactions')
  .columns({
    id: string(),
    batchId: string().from('batch_id'),
    rowNumber: number().from('row_number'),
    transactionDate: number().from('transaction_date'),
    amountCents: number().from('amount_cents'),
    rawData: json<Record<string, string>>().from('raw_data'),
    sourceFingerprint: string().from('source_fingerprint'),
    matchedUserId: string().from('matched_user_id'),
    paymentId: string().from('payment_id'),
    matchConfidence: number().from('match_confidence'),
    importedAt: number().from('imported_at'),
  })
  .primaryKey('id')

// ─────────────────────────────────────────────────────────────────────────────
// Relationships (used by queries to pull related rows, e.g. a drink's details).
// ─────────────────────────────────────────────────────────────────────────────

const userRelationships = relationships(users, ({ one, many }) => ({
  private: one({ sourceField: ['id'], destField: ['userId'], destSchema: usersPrivate }),
  profile: one({ sourceField: ['id'], destField: ['userId'], destSchema: usersProfile }),
  pin: one({ sourceField: ['id'], destField: ['userId'], destSchema: pins }),
  drinks: many({ sourceField: ['id'], destField: ['userId'], destSchema: userDrinks }),
  fees: many({ sourceField: ['id'], destField: ['userId'], destSchema: fees }),
  payments: many({ sourceField: ['id'], destField: ['userId'], destSchema: payments }),
  credits: many({ sourceField: ['id'], destField: ['userId'], destSchema: credits }),
  guests: many({ sourceField: ['id'], destField: ['userId'], destSchema: guests }),
  uploadedImportBatches: many({
    sourceField: ['id'],
    destField: ['uploadedBy'],
    destSchema: importBatches,
  }),
  importTransactions: many({
    sourceField: ['id'],
    destField: ['matchedUserId'],
    destSchema: importTransactions,
  }),
}))

const usersPrivateRelationships = relationships(usersPrivate, ({ one }) => ({
  user: one({ sourceField: ['userId'], destField: ['id'], destSchema: users }),
}))

const usersProfileRelationships = relationships(usersProfile, ({ one }) => ({
  user: one({ sourceField: ['userId'], destField: ['id'], destSchema: users }),
}))

const pinsRelationships = relationships(pins, ({ one }) => ({
  user: one({ sourceField: ['userId'], destField: ['id'], destSchema: users }),
}))

const userDrinksRelationships = relationships(userDrinks, ({ one }) => ({
  drink: one({ sourceField: ['drinkId'], destField: ['drinkId'], destSchema: drinks }),
  user: one({ sourceField: ['userId'], destField: ['id'], destSchema: users }),
  guest: one({ sourceField: ['guestId'], destField: ['id'], destSchema: guests }),
}))

const guestRelationships = relationships(guests, ({ one }) => ({
  user: one({ sourceField: ['userId'], destField: ['id'], destSchema: users }),
}))

const feesRelationships = relationships(fees, ({ one }) => ({
  user: one({ sourceField: ['userId'], destField: ['id'], destSchema: users }),
}))

const paymentsRelationships = relationships(payments, ({ one }) => ({
  user: one({ sourceField: ['userId'], destField: ['id'], destSchema: users }),
  importTransaction: one({
    sourceField: ['paymentId'],
    destField: ['paymentId'],
    destSchema: importTransactions,
  }),
}))

const creditsRelationships = relationships(credits, ({ one }) => ({
  user: one({ sourceField: ['userId'], destField: ['id'], destSchema: users }),
}))

const importBatchesRelationships = relationships(importBatches, ({ one, many }) => ({
  uploader: one({ sourceField: ['uploadedBy'], destField: ['id'], destSchema: users }),
  transactions: many({
    sourceField: ['id'],
    destField: ['batchId'],
    destSchema: importTransactions,
  }),
}))

const importTransactionsRelationships = relationships(importTransactions, ({ one }) => ({
  batch: one({ sourceField: ['batchId'], destField: ['id'], destSchema: importBatches }),
  user: one({ sourceField: ['matchedUserId'], destField: ['id'], destSchema: users }),
  payment: one({ sourceField: ['paymentId'], destField: ['paymentId'], destSchema: payments }),
}))

export const schema = createSchema({
  tables: [
    users,
    usersPrivate,
    usersProfile,
    pins,
    drinks,
    guests,
    payments,
    userDrinks,
    fees,
    credits,
    orderActivity,
    importBatches,
    importTransactions,
  ],
  relationships: [
    userRelationships,
    usersPrivateRelationships,
    usersProfileRelationships,
    pinsRelationships,
    userDrinksRelationships,
    guestRelationships,
    feesRelationships,
    paymentsRelationships,
    creditsRelationships,
    importBatchesRelationships,
    importTransactionsRelationships,
  ],
})

export type Schema = typeof schema

// Convenient row types for the UI and server (derived from the built schema).
export type UserRow = Row<Schema['tables']['users']>
export type UserPrivateRow = Row<Schema['tables']['usersPrivate']>
export type UserProfileRow = Row<Schema['tables']['usersProfile']>
export type PinRow = Row<Schema['tables']['pins']>
export type DrinkRow = Row<Schema['tables']['drinks']>
export type GuestRow = Row<Schema['tables']['guests']>
export type UserDrinkRow = Row<Schema['tables']['userDrinks']>
export type FeeRow = Row<Schema['tables']['fees']>
export type PaymentRow = Row<Schema['tables']['payments']>
export type CreditRow = Row<Schema['tables']['credits']>
export type OrderActivityRow = Row<Schema['tables']['orderActivity']>
export type ImportBatchRow = Row<Schema['tables']['importBatches']>
export type ImportTransactionRow = Row<Schema['tables']['importTransactions']>
